import React, { useState, useEffect, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import "./home.css";
import FeaturedPastEvents from "../components/FeaturedPastEvents.jsx";
import EventsShowcase from "../components/EventsShowcase.jsx";
import UpcomingEventsSection from "../components/UpcomingEventsSection.jsx";
import LiveStreamPip from "../components/LiveStreamPip.jsx";


// Lazy-load heavy subcomponents to reduce initial bundle
const Gallery = React.lazy(() => import("./Gallery"));
const Blogs = React.lazy(() => import("./Blogs"));
const Chatbot = React.lazy(() => import("./Chatbot"));


// Move static data out of component so it's not recreated on every render.
// These are FALLBACKS: the live slides/sponsors are fetched from the admin CMS
// (/api/hero-slides, /api/sponsors) and only fall back to these when empty.
const HERO_FALLBACK = [


{
  src: "/esmaaash_web/frontend/public/images/FCbanner.jpeg",
  link: "https://esmaaash.in/esmaaash-classico/register",
  alt: "Smaaash Esmaaash-classico",
  // Editable per-slide hero copy (falls back to brand defaults below)
  title: "eSMAAASH",
  tagline: "Live games. Real adrenaline.",
}
];

// ---- Hero "live broadcast" HUD config ----
// The LIVE chip + viewer count are driven by the real YouTube live status
// (/api/live/status). The event countdown now lives in <HeroEvents/>, fed by
// the dummy events DB (/api/events).

// "12453" -> "12,453" (Indian grouping). Returns "" for empty/invalid input.
function fmtViews(n) {
  const v = Number(n);
  return !n || Number.isNaN(v) ? "" : v.toLocaleString("en-IN");
}
const HERO_HEADLINE_FALLBACK = "eSMAAASH";
const HERO_TAGLINE_FALLBACK = "Live games. Real adrenaline.";

// Every banner is a ~3.6:1 wide image (native 720x200), which is why the
// spotlight box uses that one common aspect ratio (see --ar in
// index-animations.css). Keep this list to similarly-shaped banners.
const PAST_EVENTS = [
  "/esmaaash_web/frontend/public/images/Chessmaster_Hyd.webp",
  "/esmaaash_web/frontend/public/images/SMAAASH_chessmaster_hyd.webp",
  "/esmaaash_web/frontend/public/images/SMAAASH_Chessmaster_Manglore.webp",
  "/esmaaash_web/frontend/public/images/Smaaashday2025.webp",
  { src: "/esmaaash_web/frontend/public/images/Redzone.webp", title: "Crimson" },
  "/esmaaash_web/frontend/public/images/smaaash-league.webp",
  "/esmaaash_web/frontend/public/images/smaaashday-Mumbai.webp",
  "/esmaaash_web/frontend/public/images/SMAAASH_Chessmaster_Noida.webp",
  "/esmaaash_web/frontend/public/images/smaaashday-DelhiNCR.webp",
  "/esmaaash_web/frontend/public/images/SMAAASH_Chessmaster_Delhi.webp",
  "/esmaaash_web/frontend/public/images/smaaashday-Barnala.webp",
  "/esmaaash_web/frontend/public/images/Chessmaster-Mumbai.webp",
  "/esmaaash_web/frontend/public/images/pixel-challenge.webp",
];

const SPONSORS_FALLBACK = [
  "/sponsors/Easymytrip.webp",
  "/sponsors/TheIndian.webp",
  "/sponsors/Oaksmith.webp",
  "/sponsors/YESSECURITIES.webp",
  "/sponsors/Mechanix.webp",
  "/sponsors/ABCoffee.webp",
  "/sponsors/Emotorad.webp",
  "/sponsors/FreepressGe.webp",
  "/sponsors/Parle.webp",
  "/sponsors/Radio.webp",
  "/sponsors/Reliance.webp",
];

export default function Home() {

  const [liveInfo, setLiveInfo] = useState(null);

  // Hero slides + sponsors come from the admin CMS; fall back to the static
  // lists above when the API is empty/unreachable (no visual regression).
  const [images, setImages] = useState(HERO_FALLBACK);
  const [sponsorLogos, setSponsorLogos] = useState(SPONSORS_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/hero-slides")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && Array.isArray(d) && d.length) setImages(d);
      })
      .catch(() => {});
    fetch("/api/sponsors")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && Array.isArray(d) && d.length) setSponsorLogos(d.map((s) => s.src));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Real YouTube live status for the hero HUD (same endpoint the floating PiP
  // uses). When live → concurrent viewers; otherwise → latest past stream views.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/live/status")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setLiveInfo(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Hero HUD live state (real, from /api/live/status).
  const isLive = liveInfo && liveInfo.live === true;
  const pastTop =
    !isLive && liveInfo && Array.isArray(liveInfo.past) && liveInfo.past.length
      ? liveInfo.past[0]
      : null;
  const viewCount = isLive
    ? fmtViews(liveInfo.viewers)
    : fmtViews(pastTop && pastTop.views);
  const viewerLabel = viewCount
    ? `👁 ${viewCount} ${isLive ? "watching" : "views"}`
    : "";








  return (

 

    <div className="esports-bg flex flex-col items-center justify-center py-6 text-white">


      {/* ====== SEO Meta + Canonical ====== */}
      <Helmet>
        <title>Esmaaash | Sports, Gaming & Events in India</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="robots" content="index, follow" />
<meta name="author" content="Esmaaash Entertainment" />
<meta name="language" content="en-IN" />

        <meta
          name="description"
          content="Join Esmaaash for thrilling events, esports, ambassador programs, and gaming experiences across India. Explore upcoming events, past highlights, and register today."
        />
        <meta
          name="keywords"
          content="Esmaaash, sports events India, gaming tournaments, ambassador club, esports events, bowling championship"
        />
        <link rel="canonical" href="https://esmaaash.in/" />

        {/* Open Graph */}
        <meta property="og:title" content="Esmaaash | Sports, Gaming & Events in India" />
        <meta property="og:description" content="Explore upcoming tournaments, ambassador programs, and gaming events with Esmaaash." />
        <meta property="og:image" content="https://esmaaash.in/images/bowlingt2.jpg" />
        <meta property="og:url" content="https://esmaaash.in/" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Esmaaash | Sports, Gaming & Events in India" />
        <meta name="twitter:description" content="Gaming, esports, ambassador club & tournaments. Be part of Esmaaash now!" />
        <meta name="twitter:image" content="https://esmaaash.in/images/bowlingt2.jpg" />

        {/* JSON-LD Organization Schema */}
       <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Esmaaash",
      url: "https://esmaaash.in",
      logo: "https://esmaaash.in/images/logo.png",
      sameAs: [
        "https://www.facebook.com/esmaaash",
        "https://www.instagram.com/esmaaash",
        "https://twitter.com/esmaaash",
      ],
    }),
  }}
/>


      </Helmet>

<h1 className="sr-only">Esmaaash – India’s biggest sports, gaming & esports platform</h1>

      {/* ====== Events showcase — the animated hero banner and the upcoming
              events strip share ONE events source (/api/events) and ONE active
              event, so they stay in sync and auto-scroll together. ====== */}
      <EventsShowcase
        fallbackSlides={images}
        viewerLabel={viewerLabel}
        streamLive={isLive}
      />

      {/* Dedicated Upcoming Events section — fetches /api/events and shows each
          event's name with a live days:hrs:min:sec countdown. */}
    {/*  <UpcomingEventsSection />
 */}
      {/* Floating live mini-player — only shows when eSmaaash is live */}
      <LiveStreamPip />

      {/* Past Events — premium spotlight deck (focus on top, rest below) */}
   {/*   <FeaturedPastEvents events={PAST_EVENTS} />
*/}
 {/* Partners — the whole roster revealed at once (wall of partners) */}
 
  {/* Gallery */}
      <div className="section-wrapper no-reveal">
        <Suspense fallback={<div className="min-h-[200px] flex items-center justify-center">Loading…</div>}>
          <Gallery />
        </Suspense>
      </div>
      
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-text="OUR PARTNERS"
        className="text-3xl font-bold mt-16 mb-2"
      >
        OUR PARTNERS
      </motion.h2>
      <motion.p
        className="partners-sub"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        {sponsorLogos.length} powerhouse brands. One arena.
      </motion.p>

      <div className="section-wrapper">
        <div className="partners-marquee">
          <div className="partners-track">
            {[...sponsorLogos, ...sponsorLogos].map((logo, index) => (
              <div
                key={index}
                className="partner-tile"
                aria-hidden={index >= sponsorLogos.length ? "true" : undefined}
              >
                <img
                  src={logo}
                  alt="Partner"
                  className="partner-logo"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </div>


<h2 className="sr-only">Latest Articles & Blogs</h2>

      {/* Blogs */}
      <div className="section-wrapper no-reveal">
        <Suspense fallback={<div className="py-12 text-center">Loading articles…</div>}>
          <Blogs />
        </Suspense>
      </div>
<h2 className="sr-only">Esmaaash Event Gallery</h2>

     

      {/* Chatbot */}
      <Suspense fallback={<div className="hidden"></div>}>
  <Chatbot />
</Suspense>

    </div>

  );
}